<template>
  <div class="flex flex-col h-full mt-4">
    <!-- Enhanced Header Bar -->
    <div class="bg-white p-4 mb-4 rounded-lg shadow">
      <div class="flex flex-col gap-4">
        <!-- Top row with scan and repair button -->
        <div class="flex justify-between items-center">
          <div class="text-xl font-semibold text-gray-800">
            Workshop Repairs
          </div>
          <div class="flex items-center gap-3">
            <div class="w-48">
              <InputText
                id="return"
                v-model="returnValue"
                placeholder="Scan Asset..."
                type="text"
                class="w-full"
              />
            </div>
            <Button
              label="Repair"
              icon="pi pi-plus"
              severity="secondary"
              raised
              @click="openAddDialog"
            />
          </div>
        </div>

        <!-- Filters row -->
        <div class="flex gap-4 items-center">
          <div class="flex-1">
            <span class="p-float-label">
              <Dropdown
                v-model="selectedDepartment"
                :options="departmentOptions"
                option-label="name"
                placeholder="Select Department"
                class="w-full"
                @change="handleDepartmentChange"
              />
              <label>Department</label>
            </span>
          </div>
          <div class="flex-1">
            <span class="p-float-label">
              <Dropdown
                v-model="selectedMasterCategory"
                :options="masterCategoryOptions"
                option-label="name"
                placeholder="Select Master Category"
                class="w-full"
                :disabled="!selectedDepartment"
                @change="handleMasterCategoryChange"
              />
              <label>Category</label>
            </span>
          </div>
          <div class="flex-1">
            <span class="p-float-label">
              <Dropdown
                v-model="selectedCategory"
                :options="categoryOptions"
                option-label="name"
                placeholder="Select Category"
                class="w-full"
                :disabled="!selectedMasterCategory"
              />
              <label>Sub-Category</label>
            </span>
          </div>
          <Button
            icon="pi pi-filter-slash"
            severity="secondary"
            text
            tooltip="Clear Filters"
            @click="clearFilters"
          />
        </div>
      </div>
    </div>

    <!-- TreeTable section -->
    <div class="flex-1 bg-white rounded-lg shadow overflow-hidden">
      <TreeTable
        :value="filteredRepairs"
        class="h-full"
        :scrollable="true"
        scroll-height="flex"
        :expanded-keys="expandedKeys"
        show-gridlines
        responsive-layout="scroll"
        :resizable-columns="false"
        column-resize-mode="fit"
        selection-mode="single"
        @nodeSelect="onRowClick"
      >
        <template #empty>
          <div class="text-center py-8 text-gray-500">
            <p>No repairs have been added.</p>
          </div>
        </template>

        <Column
          field="name"
          header="Item Details"
          expander
          :style="{ width: '35%', minWidth: '350px' }"
        >
          <template #body="{ node }">
            <span
              :class="{
                'font-semibold text-lg': node.level === 0,
                'font-medium text-md': node.level === 1,
                'text-md': node.level === 2,
                'text-sm': !node.children
              }"
            >
              {{ node.data.name }}
            </span>
            <span
              v-if="!node.children"
              class="text-sm text-gray-500 font-mono mt-1"
            >
              {{ node.data.barcode }}
            </span>
          </template>
        </Column>
        <Column
          field="equipmentValue"
          header="Value"
          :style="{ width: '10%', minWidth: '100px' }"
        >
          <template #body="{ node }">
            <span
              v-if="!node.children"
              class="whitespace-nowrap"
            >
              {{ formatCurrency(node.data.equipmentValue) }}
            </span>
          </template>
        </Column>
        <Column
          field="fault"
          header="Fault"
          :style="{ width: '30%', minWidth: '200px' }"
        >
          <template #body="{ node }">
            <div
              v-if="!node.children"
              class="max-w-md truncate"
            >
              {{ node.data.fault }}
            </div>
          </template>
        </Column>
        <Column
          field="logDate"
          header="Logged Date"
          :style="{ width: '10%', minWidth: '120px' }"
        >
          <template #body="{ node }">
            <span
              v-if="!node.children"
              class="whitespace-nowrap"
            >{{ node.data.logDate }}</span>
          </template>
        </Column>
        <Column
          field="logPerson"
          header="Logged By"
          :style="{ width: '10%', minWidth: '120px' }"
        >
          <template #body="{ node }">
            <span v-if="!node.children">{{ node.data.logPerson }}</span>
          </template>
        </Column>
        <Column
          field="status"
          header="Status"
          :style="{ width: '5%', minWidth: '100px' }"
        >
          <template #body="{ node }">
            <Tag
              v-if="!node.children"
              :value="node.data.status"
              :severity="getStatusSeverity(node.data.status)"
            />
          </template>
        </Column>
        <Column
          header="Actions"
          :style="{ width: '5%', minWidth: '80px' }"
        >
          <template #body="{ node }">
            <div
              v-if="!node.children"
              class="flex justify-center"
            >
              <Button
                icon="pi pi-eye"
                text
                severity="secondary"
                tooltip="View Details"
                @click="navigateToRepair(node.data.id)"
              />
            </div>
          </template>
        </Column>
      </TreeTable>
    </div>

    <!-- Modals -->
    <ReusableDialog
      v-model="isAddRepairVisible"
      header="Add Repair"
      class="max-w-2xl"
    >
      <AddRepair
        :barcode="returnValue"
        @submit="handleAddRepair"
      />
    </ReusableDialog>

    <ReusableDialog
      v-model="isViewVisible"
      header="View Repair"
      class="max-w-2xl"
    >
      <ViewRepair :data="repairData" />
    </ReusableDialog>
  </div>
</template>

<script setup>
import ReusableDialog from '@/components/shared/dialog/dialogComponent.vue';
import { useDialog } from '@/composables/useDialog';
import AddRepair from '@/components/workshop/CreateRepair.vue';
import ViewRepair from '@/components/workshop/ViewRepair.vue';
import { ref, computed, onMounted } from 'vue';
import { createRepairs } from '@/data/faker';
import { useRouter } from 'vue-router';

// Dialog Control Using `useDialog`
const { isVisible: isAddRepairVisible, openDialog: openAddDialog, closeDialog: closeAddDialog } = useDialog();
const isViewVisible = ref(false);

// Data References
const repairs = ref([]);
const repairData = ref(null);
const returnValue = ref('');
const expandedKeys = ref({});

// Filter references
const selectedDepartment = ref(null);
const selectedMasterCategory = ref(null);
const selectedCategory = ref(null);

// Currency formatter
const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP'
    }).format(value || 0);
};

// Status handling
const getStatusSeverity = (status) => {
    switch (status?.toLowerCase()) {
        case 'completed':
            return 'success';
        case 'in progress':
            return 'info';
        case 'pending':
            return 'warning';
        case 'cancelled':
            return 'danger';
        default:
            return null;
    }
};

// Computed options for dropdowns
const departmentOptions = computed(() => {
    const departments = [...new Set(repairs.value.map(r => r.department))];
    return departments.map(dept => ({ name: dept }));
});

const masterCategoryOptions = computed(() => {
    if (!selectedDepartment.value) return [];
    const categories = [...new Set(repairs.value
        .filter(r => r.department === selectedDepartment.value.name)
        .map(r => r.masterCategory))];
    return categories.map(cat => ({ name: cat }));
});

const categoryOptions = computed(() => {
    if (!selectedMasterCategory.value) return [];
    const categories = [...new Set(repairs.value
        .filter(r => r.department === selectedDepartment.value.name
            && r.masterCategory === selectedMasterCategory.value.name)
        .map(r => r.category))];
    return categories.map(cat => ({ name: cat }));
});

// Filter handlers
const handleDepartmentChange = () => {
    selectedMasterCategory.value = null;
    selectedCategory.value = null;
};

const handleMasterCategoryChange = () => {
    selectedCategory.value = null;
};

const clearFilters = () => {
    selectedDepartment.value = null;
    selectedMasterCategory.value = null;
    selectedCategory.value = null;
};

// Filtered repairs
const filteredRepairs = computed(() => {
    let filtered = repairs.value;

    if (selectedDepartment.value) {
        filtered = filtered.filter(r => r.department === selectedDepartment.value.name);
    }
    if (selectedMasterCategory.value) {
        filtered = filtered.filter(r => r.masterCategory === selectedMasterCategory.value.name);
    }
    if (selectedCategory.value) {
        filtered = filtered.filter(r => r.category === selectedCategory.value.name);
    }

    // Reorganize filtered repairs into tree structure
    const tree = [];
    const departments = {};

    filtered.forEach(repair => {
        const dept = repair.department || 'Uncategorized';
        const masterCat = repair.masterCategory || 'Uncategorized';
        const cat = repair.category || 'Uncategorized';

        if (!departments[dept]) {
            departments[dept] = {
                key: `dept-${dept}`,
                data: { name: dept },
                children: []
            };
            tree.push(departments[dept]);
        }

        let masterCatNode = departments[dept].children.find(
            child => child.data.name === masterCat
        );
        if (!masterCatNode) {
            masterCatNode = {
                key: `master-${dept}-${masterCat}`,
                data: { name: masterCat },
                children: []
            };
            departments[dept].children.push(masterCatNode);
        }

        let catNode = masterCatNode.children.find(
            child => child.data.name === cat
        );
        if (!catNode) {
            catNode = {
                key: `cat-${dept}-${masterCat}-${cat}`,
                data: { name: cat },
                children: []
            };
            masterCatNode.children.push(catNode);
        }

        catNode.children.push({
            key: `repair-${repair.id}`,
            data: repair
        });
    });

    return tree;
});

// Populate repairs when the component is mounted
onMounted(() => {
    repairs.value = createRepairs(50).map(repair => ({
        ...repair,
        department: ['Audio', 'Lighting', 'Video', 'Rigging'][Math.floor(Math.random() * 4)],
        masterCategory: ['Sound', 'LED', 'Projection', 'Motors'][Math.floor(Math.random() * 4)],
        category: ['Speakers', 'Panels', 'Screens', 'Chain Hoists'][Math.floor(Math.random() * 4)],
        status: ['Completed', 'In Progress', 'Pending', 'Cancelled'][Math.floor(Math.random() * 4)],
        equipmentValue: Math.random() * 10000
    }));

    // Initialize with no expanded nodes
    expandedKeys.value = {};
});

// Add router instance
const router = useRouter();

// Update click handler
const onRowClick = (event) => {
    if (!event.data.children) { // Only navigate for actual repair items, not category headers
        router.push(`/workshop/repairs/${event.data.id}`);
    }
};

// Add navigation function for the eye icon button
const navigateToRepair = (id) => {
    router.push(`/workshop/repairs/${id}`);
};

// Handle Adding a New Repair
const handleAddRepair = (newRepair) => {
    repairs.value.unshift({
        ...newRepair,
        id: Date.now(), // Temporary ID generation
        logDate: new Date().toLocaleDateString(),
        logPerson: 'John Doe',
        status: 'Pending',
        equipmentValue: newRepair.equipmentValue || 0
    });
    closeAddDialog();
    returnValue.value = '';
};
</script>

<style>
/* Custom styles for TreeTable that can't be done with Tailwind */
:deep(.p-treetable) {
    @apply h-full flex flex-col;
}

:deep(.p-treetable-wrapper) {
    @apply flex-1 overflow-auto;
}

:deep(.p-treetable-table) {
    @apply min-w-full;
}

/* Header Styling */
:deep(.p-treetable-thead > tr > th) {
    @apply bg-slate-50 p-4 font-semibold text-slate-700 text-sm uppercase tracking-wider border-b-2 border-slate-200;
}

/* Hover effect on header */
:deep(.p-treetable-thead > tr > th:hover) {
    @apply bg-slate-100;
}

/* Sort icons */
:deep(.p-sortable-column-icon) {
    @apply w-3 h-3 ml-2;
}

/* Active sort column */
:deep(.p-sortable-column.p-highlight) {
    @apply bg-slate-100 text-blue-600;
}

/* Add cursor pointer and hover effect for repair rows */
:deep(.p-treetable-tbody > tr:not([data-pc-section="expansion"]):hover) {
    @apply bg-slate-50 cursor-pointer;
}

/* Make repair rows visually distinct */
:deep(.p-treetable-tbody > tr:not([data-pc-section="expansion"])) {
    @apply transition-colors duration-200;
}

/* Column resize indicator */
:deep(.p-column-resizer-helper) {
    @apply bg-blue-500;
}
</style>
